<template>
  <div v-if="waitingGame?.length" class="card mb-3 user-games-section">
    <div class="card-header pb-0">
      <h5>Přihlášen do her</h5>
    </div>
    <div class="card-body px-2 pt-2 pb-0">
      <div class="game-table-flush">
        <vue-good-table
          styleClass="vgt-table condensed vgt-flush"
          v-bind="tableOptions"
          :columns="columns.waitingGameTableColumns"
          :rows="waitingGame"
          :pagination-options="paginationFor(waitingGame)"
          compactMode
        >
          <template #emptystate>
            <div class="text-center">
              <span>Žádné čekající hry</span>
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>
  </div>

  <div v-if="runningGame?.length" class="card mb-3 user-games-section">
    <div class="card-header pb-0">
      <h5>Právě hraje</h5>
    </div>
    <div class="card-body px-2 pt-2 pb-0">
      <div class="game-table-flush">
        <vue-good-table
          styleClass="vgt-table condensed vgt-flush"
          v-bind="tableOptions"
          :columns="columns.runningGameTableColumns"
          :rows="runningGame"
          :pagination-options="paginationFor(runningGame)"
          :row-click="true"
          @row-click="goToRunningGame"
          compactMode
        >
          <template #table-row="props">
            <button
              v-if="props.column.field == 'action'"
              class="my-auto btn btn-link btn-icon-only btn-rounded btn-sm text-dark icon-move-right"
            >
              <i class="ni ni-bold-right" aria-hidden="true"></i>
            </button>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
          <template #emptystate>
            <div class="text-center">
              <span>Žádné běžící hry</span>
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>
  </div>

  <div v-if="finishedGame?.length" class="card mb-3 user-games-section">
    <div class="card-header pb-0">
      <h5>Ukončené hry</h5>
    </div>
    <div class="card-body px-2 pt-2 pb-0">
      <div class="game-table-flush">
        <vue-good-table
          styleClass="vgt-table condensed vgt-flush"
          v-bind="tableOptions"
          :columns="columns.finishedGameTableColumns"
          :rows="finishedGame"
          :pagination-options="paginationFor(finishedGame)"
          :sort-options="finishedSortOptions"
          :row-click="true"
          @row-click="goToFinishedGame"
          compactMode
        >
          <template #table-row="props">
            <button
              v-if="props.column.field == 'action'"
              class="my-auto btn btn-link btn-icon-only btn-rounded btn-sm text-dark icon-move-right"
            >
              <i class="ni ni-bold-right" aria-hidden="true"></i>
            </button>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
          <template #emptystate>
            <div class="text-center">
              <span>Žádné ukončené hry</span>
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>
  </div>

  <div
    v-if="!waitingGame?.length && !runningGame?.length && !finishedGame?.length"
    class="card"
  >
    <div class="card-body text-center py-5">
      <h6 class="mb-0">Tento hráč zatím nemá žádné veřejné herní záznamy.</h6>
    </div>
  </div>
</template>

<script setup>
import { reactive } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { tableOptions, paginationFor, czechDateColumn } from "../../../helpers/tableOptions";

const props = defineProps({
  finishedGame: Array,
  runningGame: Array,
  waitingGame: Array,
  userDetail: [Array, Object]
})

const store = useStore();
const router = useRouter();

const formatFn = (value) => {
  return store.getters.formatTableNumber(value);
}

const finishedSortOptions = {
  enabled: true,
  initialSortBy: { field: 'GameTo', type: 'desc' }
};

const goToRunningGame = (params) => {
  router.push({name: "Běžící hra", query: { hrac: props.userDetail.UserNickName }, params: { gid: params.row.GameID, pid: props.userDetail.UserID }})
}

const goToFinishedGame = (params) => {
  router.push({name: "Ukončená hra", query: { hrac: props.userDetail.UserNickName }, params: { gid: params.row.GameID, pid: props.userDetail.UserID }})
}

const columns = reactive({
  finishedGameTableColumns: [
    {
      label: "Konec hry",
      field: "GameTo",
      ...czechDateColumn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "100px",
    },
    {
      label: "Hra",
      field: "GameName",
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "280px",
    },
    {
      label: "Pořadí",
      field: "Rank",
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder text-center",
      tdClass: "text-sm font-weight-bold text-center",
      width: "150px",
    },
    {
      label: "Výhra",
      field: "Reward",
      type: "number",
      formatFn: formatFn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "140px",
    },
    {
      label: "Hodnota portfolia",
      field: "PortfolioAmount",
      type: "number",
      formatFn: formatFn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
    },
    {
      label: "Detail",
      field: "action",
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder text-end",
      tdClass: "text-sm font-weight-bold text-end",
    },
  ],
  waitingGameTableColumns: [
    {
      label: "Začátek hry",
      field: "GameFrom",
      ...czechDateColumn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "100px",
    },
    {
      label: "Hra",
      field: "GameName",
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "280px",
    },
    {
      label: "Kapitál",
      field: "GameCapital",
      formatFn: formatFn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
    },
  ],
  runningGameTableColumns: [
    {
      label: "Konec hry",
      field: "GameTo",
      ...czechDateColumn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "100px",
    },
    {
      label: "Hra",
      field: "GameName",
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "280px",
    },
    {
      label: "Pořadí",
      field: "Rank",
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder text-center",
      tdClass: "text-sm font-weight-bold text-center",
      width: "150px",
    },
    {
      label: "Výhra",
      field: "Reward",
      formatFn: formatFn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
      width: "140px",
    },
    {
      label: "Hodnota portfolia",
      field: "PortfolioAmount",
      formatFn: formatFn,
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder",
      tdClass: "text-sm font-weight-bold",
    },
    {
      label: "Detail",
      field: "action",
      thClass: "text-uppercase text-dark text-xxs font-weight-bolder text-center",
      tdClass: "text-sm font-weight-bold text-center",
    },
  ],
});
</script>
